import { useEffect, useState } from "react";
import { businessListingCategoryOptions } from "../../constants/gigCategories";
import type {
  BusinessListingJobType,
  BusinessListingPayType,
  BusinessListingResponse,
  BusinessListingStatus,
  CreateBusinessListingRequest,
} from "../../types/businessListingTypes";
import type { LocationType } from "../../types/gigTypes";
import { parseUsdInputToCents } from "../../utils/GigPricingUtils";
import {
  businessListingJobTypeOptions,
  businessListingPayTypeOptions,
  formatBusinessListingCompensation,
  formatBusinessListingDuration,
} from "../../utils/BusinessListingUtils";
import Combobox from "../ui/Combobox/Combobox";
import InfoTooltip from "../ui/InfoTooltip/InfoTooltip";
import Modal from "../ui/Modal/Modal";
import Button from "../ui/Button/Button";
import UsLocationFields from "../ui/UsLocationFields/UsLocationFields";

type CreateBusinessListingModalProps = {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateBusinessListingRequest) => Promise<void>;
  initialValues?: BusinessListingResponse | null;
  modalTitle?: string;
  publishLabel?: string;
  publishingLabel?: string;
  draftLabel?: string;
  draftingLabel?: string;
  allowDraftAction?: boolean;
  primaryActionStatus?: BusinessListingStatus;
  chargesPublicationFee?: boolean;
};

const sanitizePaymentInput = (value: string) => {
  if (value === "") {
    return "";
  }

  if (!/^\d*\.?\d*$/.test(value)) {
    return null;
  }

  const decimalIndex = value.indexOf(".");
  if (decimalIndex === -1) {
    return value;
  }

  const whole = value.slice(0, decimalIndex);
  const fraction = value.slice(decimalIndex + 1, decimalIndex + 3);
  return `${whole}.${fraction}`;
};

const volunteerPayType: BusinessListingPayType = "ONE_TIME";
const volunteerPayAmountCents = 100;

const CreateBusinessListingModal = ({
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
  initialValues = null,
  modalTitle = "Create Job Listing",
  publishLabel = "Pay $19.99 & Publish",
  publishingLabel = "Starting Checkout...",
  draftLabel = "Save Draft",
  draftingLabel = "Saving Draft...",
  allowDraftAction = true,
  primaryActionStatus = "PUBLISHED",
  chargesPublicationFee = true,
}: CreateBusinessListingModalProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [jobType, setJobType] = useState<BusinessListingJobType>("PART_TIME");
  const [category, setCategory] = useState("");
  const [locationType, setLocationType] = useState<LocationType>("IN_PERSON");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [payType, setPayType] = useState<BusinessListingPayType>("HOURLY");
  const [minPayDollars, setMinPayDollars] = useState("");
  const [maxPayDollars, setMaxPayDollars] = useState("");
  const [estimatedHours, setEstimatedHours] = useState("");
  const [communityServiceHoursEligible, setCommunityServiceHoursEligible] = useState(false);
  const [communityServiceProofDetails, setCommunityServiceProofDetails] = useState("");
  const [submitStatus, setSubmitStatus] = useState<BusinessListingStatus>("PUBLISHED");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setTitle(initialValues?.title ?? "");
    setDescription(initialValues?.description ?? "");
    setJobType(initialValues?.jobType ?? "PART_TIME");
    setCategory(initialValues?.category ?? "");
    setLocationType(initialValues?.locationType ?? "IN_PERSON");
    setCity(initialValues?.city ?? "");
    setState(initialValues?.state ?? "");
    setZip(initialValues?.zip ?? "");
    setPayType(initialValues?.payType ?? "HOURLY");
    setMinPayDollars(initialValues ? String((initialValues.minPayAmountCents / 100).toFixed(2)) : "");
    setMaxPayDollars(initialValues ? String((initialValues.maxPayAmountCents / 100).toFixed(2)) : "");
    setEstimatedHours(
      initialValues?.estimatedDurationMinutes
        ? String(Math.round(initialValues.estimatedDurationMinutes / 60))
        : "",
    );
    setSubmitStatus(initialValues?.status ?? "PUBLISHED");
    setCommunityServiceHoursEligible(Boolean(initialValues?.communityServiceHoursEligible));
    setCommunityServiceProofDetails(initialValues?.communityServiceProofDetails ?? "");
    setError(null);
  }, [initialValues, isOpen]);

  const minPayAmountCents = parseUsdInputToCents(minPayDollars);
  const maxPayAmountCents = parseUsdInputToCents(maxPayDollars);
  const estimatedHoursNumber = estimatedHours.trim() === "" ? 0 : Number(estimatedHours);
  const hasAnyEstimatedDurationInput = estimatedHours.trim() !== "";
  const hasValidEstimatedDuration =
    Number.isInteger(estimatedHoursNumber) && estimatedHoursNumber >= 0;
  const estimatedDurationMinutesTotal = estimatedHoursNumber * 60;
  const estimatedDurationMinutes = hasValidEstimatedDuration && hasAnyEstimatedDurationInput
    ? estimatedDurationMinutesTotal
    : null;
  const categoryIsVolunteer = category.trim().toLowerCase() === "volunteer";
  const categoryMarksVolunteerListing = categoryIsVolunteer;
  const isVolunteerListing = jobType === "VOLUNTEER" || categoryMarksVolunteerListing;
  const effectiveJobType: BusinessListingJobType = isVolunteerListing ? "VOLUNTEER" : jobType;
  const effectivePayType = isVolunteerListing ? volunteerPayType : payType;
  const effectiveMinPayAmountCents = isVolunteerListing ? volunteerPayAmountCents : minPayAmountCents;
  const effectiveMaxPayAmountCents = isVolunteerListing ? volunteerPayAmountCents : maxPayAmountCents;
  const volunteerPublicationIsFree =
    isVolunteerListing
    && chargesPublicationFee
    && primaryActionStatus === "PUBLISHED";
  const effectivePublishLabel = volunteerPublicationIsFree ? "Publish" : publishLabel;
  const effectivePublishingLabel = volunteerPublicationIsFree ? "Publishing..." : publishingLabel;
  const categoryPlaceholder = "Retail, Food Service, Volunteer";

  const handleCategoryChange = (nextCategory: string) => {
    setCategory(nextCategory);
    if (nextCategory.trim().toLowerCase() === "volunteer") {
      setJobType("VOLUNTEER");
    }
  };

  const handleLocationTypeChange = (nextLocationType: LocationType) => {
    setLocationType(nextLocationType);
    if (nextLocationType === "REMOTE") {
      setCity("");
      setState("");
      setZip("");
    }
  };

  const handleSubmit = async (nextStatus: BusinessListingStatus) => {
    setError(null);

    if (!title.trim()) {
      setError("Listing title is required.");
      return;
    }
    if (!description.trim()) {
      setError("Job description is required.");
      return;
    }
    if (!jobType) {
      setError("Job type is required.");
      return;
    }
    if (!category.trim()) {
      setError("Job category is required.");
      return;
    }
    if (locationType === "IN_PERSON" && (!city.trim() || state.trim().length !== 2 || !zip.trim())) {
      setError("Enter a valid work location.");
      return;
    }
    if (!isVolunteerListing) {
      if (minPayAmountCents === null) {
        setError("Minimum pay is required.");
        return;
      }
      if (maxPayAmountCents === null) {
        setError("Maximum pay is required.");
        return;
      }
      if (minPayAmountCents < 100) {
        setError("Minimum pay must be at least $1.00.");
        return;
      }
      if (maxPayAmountCents < 100) {
        setError("Maximum pay must be at least $1.00.");
        return;
      }
      if (maxPayAmountCents < minPayAmountCents) {
        setError("Maximum pay must be greater than or equal to minimum pay.");
        return;
      }
    }
    if (hasAnyEstimatedDurationInput && (!hasValidEstimatedDuration || estimatedDurationMinutes === null || estimatedDurationMinutes <= 0 || estimatedDurationMinutes > 1_440)) {
      setError("Usual shift length must be between 1 minute and 24 hours.");
      return;
    }
    if (effectiveMinPayAmountCents === null || effectiveMaxPayAmountCents === null) {
      setError("Pay range is required.");
      return;
    }
    if (isVolunteerListing && communityServiceHoursEligible && !communityServiceProofDetails.trim()) {
      setError("Describe the form or documentation you will provide to verify community service hours.");
      return;
    }

    setSubmitStatus(nextStatus);

    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        jobType: effectiveJobType,
        category: category.trim(),
        locationType,
        city: locationType === "IN_PERSON" ? city.trim() : null,
        state: locationType === "IN_PERSON" ? state.trim().toUpperCase() : null,
        zip: locationType === "IN_PERSON" ? zip.trim() : null,
        payType: effectivePayType,
        minPayAmountCents: effectiveMinPayAmountCents,
        maxPayAmountCents: effectiveMaxPayAmountCents,
        estimatedDurationMinutes,
        status: nextStatus,
        communityServiceHoursEligible: isVolunteerListing && communityServiceHoursEligible,
        communityServiceProofDetails: isVolunteerListing && communityServiceHoursEligible ? communityServiceProofDetails.trim() : null,
      });
      onClose();
    } catch (submitError) {
      if (submitError instanceof Error) {
        setError(submitError.message);
        return;
      }
      setError("Failed to save business listing.");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit(primaryActionStatus);
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="business-listing-job-type" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
              Job type
            </label>
            <select
              id="business-listing-job-type"
              value={jobType}
              onChange={(event) => setJobType(event.target.value as BusinessListingJobType)}
              className="h-[42px] w-full rounded-md border border-myteengig-border bg-white px-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            >
              {businessListingJobTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-myteengig-text-muted">
              Choose how this role is structured.
            </p>
          </div>
          <Combobox
            id="business-listing-category"
            label="Job category"
            value={category}
            required
            maxLength={60}
            options={businessListingCategoryOptions}
            onChange={handleCategoryChange}
            placeholder={categoryPlaceholder}
            helperText="Choose the kind of work this role covers."
          />
        </div>

        <div>
            <label htmlFor="business-listing-title" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
              Job title
            </label>
            <input
              id="business-listing-title"
              type="text"
              required
              maxLength={120}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
              placeholder="After-school counter help"
            />
            <p className="mt-1 text-xs text-myteengig-text-muted">
              Use the role or shift name applicants should recognize right away.
            </p>
        </div>

        <div>
          <label htmlFor="business-listing-description" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            Job description
          </label>
          <textarea
            id="business-listing-description"
            required
            rows={5}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            placeholder="Describe the duties, who the teen reports to, what to wear, what to bring, and any physical or customer-facing expectations."
          />
          <div className="mt-3 rounded-2xl border border-myteengig-border bg-myteengig-background/60 px-4 py-3 text-sm text-myteengig-text-muted">
            <span className="font-semibold text-myteengig-text-primary">Strong listings usually cover:</span>{" "}
            main duties, who the teen reports to, what to wear or bring, and whether the work is customer-facing,
            standing, lifting, or outdoors.
          </div>
        </div>

        {!isVolunteerListing ? (
          <div className="grid gap-4 lg:grid-cols-3">
            <div>
              <div className="mb-1 flex items-center gap-2">
                <label htmlFor="business-listing-pay-type" className="text-sm font-medium text-myteengig-text-primary">
                  Pay type
                </label>
                <InfoTooltip
                  label="Business listing payment info"
                  message="Publishing a listing has a one-time $19.99 listing fee for 3 months. Hiring, scheduling, and wages are still handled directly between your business and the teen."
                  placement="top-left"
                />
              </div>
              <select
                id="business-listing-pay-type"
                value={payType}
                onChange={(event) => setPayType(event.target.value as BusinessListingPayType)}
                className="h-[42px] w-full rounded-md border border-myteengig-border bg-white px-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
              >
                {businessListingPayTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-myteengig-text-muted">
                Choose how the compensation range should be described to applicants.
              </p>
            </div>

            <div>
              <label htmlFor="business-listing-min-pay" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
                Minimum pay (USD)
              </label>
              <input
                id="business-listing-min-pay"
                type="number"
                min="1"
                step="0.01"
                required
                value={minPayDollars}
                onChange={(event) => {
                  const nextValue = sanitizePaymentInput(event.target.value);
                  if (nextValue !== null) {
                    setMinPayDollars(nextValue);
                  }
                }}
                className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                placeholder="15.00"
              />
              <p className="mt-1 text-xs text-myteengig-text-muted">
                Enter the low end of the pay range applicants should see.
              </p>
            </div>

            <div>
              <label htmlFor="business-listing-max-pay" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
                Maximum pay (USD)
              </label>
              <input
                id="business-listing-max-pay"
                type="number"
                min="1"
                step="0.01"
                required
                value={maxPayDollars}
                onChange={(event) => {
                  const nextValue = sanitizePaymentInput(event.target.value);
                  if (nextValue !== null) {
                    setMaxPayDollars(nextValue);
                  }
                }}
                className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                placeholder="18.00"
              />
              <p className="mt-1 text-xs text-myteengig-text-muted">
                Set the compensation style and the pay range applicants should expect.
              </p>
            </div>
          </div>
        ) : null}

        {isVolunteerListing ? (
          <div className="space-y-3 rounded-2xl border border-myteengig-border bg-white px-4 py-3">
            <label className="flex items-start gap-3 text-sm font-medium text-myteengig-text-primary">
              <input
                type="checkbox"
                checked={communityServiceHoursEligible}
                onChange={(event) => setCommunityServiceHoursEligible(event.target.checked)}
                className="mt-1"
              />
              Community service hours are available for this volunteer listing
            </label>
            {communityServiceHoursEligible ? (
              <div>
                <label htmlFor="business-listing-community-service-proof" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
                  How will you verify the hours? <span aria-hidden="true">*</span>
                </label>
                <textarea
                  id="business-listing-community-service-proof"
                  required
                  maxLength={1000}
                  rows={3}
                  value={communityServiceProofDetails}
                  onChange={(event) => setCommunityServiceProofDetails(event.target.value)}
                  className="w-full rounded-md border border-myteengig-border px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                  placeholder="We will sign the teen's school service-hours form after each shift."
                />
                <p className="mt-1 text-xs text-myteengig-text-muted">Name the form or documentation you will provide. Teens will see these details.</p>
              </div>
            ) : null}
          </div>
        ) : null}

        <div>
          <label htmlFor="business-listing-estimated-hours" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            Usual shift length in hours (optional)
          </label>
          <input
            id="business-listing-estimated-hours"
            type="number"
            min="0"
            max="24"
            step="1"
            value={estimatedHours}
            onChange={(event) => setEstimatedHours(event.target.value)}
            className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            placeholder="3"
          />
          <p className="mt-1 text-xs text-myteengig-text-muted">
            Add this only if the role usually has a predictable shift length.
          </p>
        </div>

        <div className="rounded-2xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-muted">
          {effectiveMinPayAmountCents !== null && effectiveMaxPayAmountCents !== null ? (
            <>
              Applicants will see{" "}
              <span className="font-semibold text-myteengig-text-primary">
                {formatBusinessListingCompensation(effectivePayType, effectiveMinPayAmountCents, effectiveMaxPayAmountCents, effectiveJobType)}
              </span>
              .
              {estimatedDurationMinutes ? (
                <>
                  {" "}Usual shift length:{" "}
                  <span className="font-semibold text-myteengig-text-primary">
                    {formatBusinessListingDuration(estimatedDurationMinutes)}
                  </span>
                  .
                </>
              ) : null}
            </>
          ) : (
            "Applicants will see the pay range and pay type you set for this role."
          )}
        </div>

        {chargesPublicationFee && !volunteerPublicationIsFree ? (
          <div className="rounded-2xl border border-myteengig-primary/15 bg-myteengig-background px-4 py-3 text-sm text-myteengig-text-muted">
            This is a one-time <span className="font-semibold text-myteengig-text-primary">$19.99</span> listing fee. Published listings stay live for 3 months.
          </div>
        ) : null}

        <div>
          <p className="mb-2 text-sm font-medium text-myteengig-text-primary">Work location</p>
          <div className="space-y-3">
            <div
              role="group"
              aria-label="Business listing location type"
              className="grid grid-cols-2 overflow-hidden rounded-lg border border-myteengig-border bg-myteengig-background/60 p-1"
            >
              {([
                { value: "IN_PERSON", label: "In Person" },
                { value: "REMOTE", label: "Remote" },
              ] as const).map((option) => {
                const isSelected = locationType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => handleLocationTypeChange(option.value)}
                    className={`rounded-md px-3 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-myteengig-primary/30 ${
                      isSelected
                        ? "bg-myteengig-primary text-white shadow-sm"
                        : "text-myteengig-text-muted hover:bg-white hover:text-myteengig-text-primary"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {locationType === "IN_PERSON" ? (
              <UsLocationFields
                idPrefix="business-listing"
                city={city}
                state={state}
                zip={zip}
                required
                labels="visible"
                onCityChange={setCity}
                onStateChange={setState}
                onZipChange={setZip}
              />
            ) : (
              <div className="rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm font-semibold text-myteengig-text-primary">
                Remote
              </div>
            )}
          </div>
        </div>

        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          {allowDraftAction ? (
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => void handleSubmit("DRAFT")}
            >
              {isSubmitting && submitStatus === "DRAFT" ? draftingLabel : draftLabel}
            </Button>
          ) : null}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && submitStatus === primaryActionStatus ? effectivePublishingLabel : effectivePublishLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateBusinessListingModal;
